/**
 * Authentication context for managing user authentication state.
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';

interface User {
  id: string;
  email: string;
  name?: string;
  google_name?: string;
  phone_number?: string;
  phone_verified?: boolean;
  [key: string]: unknown;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isExplicitlyLoggedOut, setIsExplicitlyLoggedOut] = useState(false);
  const queryClient = useQueryClient();
  const location = useLocation();

  // Check if we're on the login page - skip auth check to avoid expected 401 errors
  const isLoginPage = location.pathname === '/login';

  // Check authentication status via API call (more secure than reading cookies)
  // This uses the httponly cookie automatically via withCredentials
  // Disable query if explicitly logged out or on login page to prevent unnecessary 401 errors
  const { data: userData, isLoading, error: authError } = useQuery({
    queryKey: ['user', location.pathname], // Include pathname in key to refetch when route changes
    queryFn: async () => {
      // Double-check we're not on login page (defense in depth)
      if (location.pathname === '/login') {
        return null;
      }

      try {
        // Try to get user with roles first (includes user_group info)
        const rolesResponse = await apiClient.getUserRoles();
        if (!rolesResponse.error && rolesResponse.data) {
          return rolesResponse.data as User;
        }
        // Fallback to basic user data
        const response = await apiClient.getUserData();
        if (response.error) {
          // 401 is expected when not authenticated - don't throw, just return null
          // This prevents error state and console noise
          if (response.error.includes('401') || response.error.includes('Unauthorized')) {
            return null;
          }
          throw new Error(response.error);
        }
        return response.data as User;
      } catch (error) {
        // Handle 401 errors gracefully - they're expected when not authenticated
        // The apiClient already converts axios errors to { error: ... }, so this
        // catch block handles any unexpected errors
        if (error instanceof Error && (error.message.includes('401') || error.message.includes('Unauthorized'))) {
          return null;
        }
        // Re-throw other errors
        throw error;
      }
    },
    retry: false,
    refetchOnWindowFocus: !isLoginPage, // Don't refetch on login page
    enabled: !isExplicitlyLoggedOut && !isLoginPage, // Don't query if explicitly logged out or on login page
    // Suppress error logging for 401s - they're expected when not authenticated
    throwOnError: false,
  });

  // Update authentication state based on API response
  useEffect(() => {
    if (userData) {
      setIsAuthenticated(true);
      setUser(userData);
      setIsExplicitlyLoggedOut(false); // Reset logout flag on successful auth
      // Fetch CSRF token for authenticated sessions
      apiClient.fetchCsrfToken().catch((err) => {
        console.warn('Failed to fetch CSRF token:', err);
      });
    } else if (authError && !isExplicitlyLoggedOut) {
      // Only update state on error if not explicitly logged out
      // (to avoid clearing state during logout process)
      setIsAuthenticated(false);
      setUser(null);
    }
  }, [userData, authError, isExplicitlyLoggedOut]);

  const login = () => {
    // Backend sets the httponly cookie, just refresh user data
    setIsExplicitlyLoggedOut(false); // Reset logout flag
    setIsAuthenticated(true);
    queryClient.invalidateQueries({ queryKey: ['user'] });
  };

  const logout = async () => {
    // Set flag to prevent query from refetching after logout
    setIsExplicitlyLoggedOut(true);

    try {
      // Only call logout API if we have a valid session (avoid 401 loop)
      // If we're already logged out, just clear local state
      if (isAuthenticated) {
        await apiClient.logoutUser();
      }
    } catch (error) {
      // Ignore 401 errors during logout (user already logged out)
      if (error instanceof Error && !error.message.includes('401')) {
        console.error('Logout error:', error);
      }
    } finally {
      // Always clear local state and query cache
      setUser(null);
      setIsAuthenticated(false);
      queryClient.setQueryData(['user'], null); // Set query data to null
      queryClient.cancelQueries({ queryKey: ['user'] }); // Cancel any in-flight queries
    }
  };

  const refreshUser = async () => {
    await queryClient.invalidateQueries({ queryKey: ['user'] });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
